import React from 'react'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import VisibilityIcon from '@material-ui/icons/Visibility'
import StarIcon from '@material-ui/icons/Star'
import Button from '@material-ui/core/Button'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import Tooltip from '@material-ui/core/Tooltip'
import MaterialLink from '@material-ui/core/Link'
import CardMedia from '@material-ui/core/CardMedia'
import {
  TranslationDictionary,
  dateFormatMask,
  format,
  moneyFormat,
  useLocale
} from '@plandi/common'
import { components } from '@plandi/common/src/generated'
import Link from 'next/link'
import clsx from 'clsx'

import { URLS } from 'src/common'
import { useProjectCardOwnerStyles } from './project-card-owner.styles'
import {
  useMutationDraftProject,
  useMutationPublishProject
} from '../project-mutations'
import { ProjectAuthorMenu } from '../project-author-menu'

type ProjectCardOwnerProps = components['schemas']['ProjectMarketMeResource']
type Props = {
  translate?: TranslationDictionary['data']
}

export const ProjectCardOwner: React.FC<ProjectCardOwnerProps & Props> = (
  props
) => {
  const classes = useProjectCardOwnerStyles()
  const locale = useLocale()
  const isPublish = props.projectStatus === 'published'

  const tooltipInvalidateProject = !props.availableForPublication
    ? props.translate?.['project-required-fields']
    : ''

  const tooltipBlockedProject = props.isBlocked
    ? props.translate?.market?.['not-can-publish-projet'] ??
      'Проект не может быть опубликован потому что был заблокирован за нарушение правил'
    : ''

  const mutationPublishProject = useMutationPublishProject(props.translate)
  const mutationDraftProject = useMutationDraftProject(props.translate)

  const handleDraftProject = () => {
    if (props.id) {
      mutationDraftProject.mutate(props.id)
    }
  }

  const handlePublishProject = () => {
    if (props.id) {
      mutationPublishProject.mutate(props.id)
    }
  }

  const renderImage = () => {
    if (props.cover) {
      return (
        <CardMedia
          image={props.cover ?? undefined}
          title={props.name ?? ''}
          className={classes.img}
          component="img"
        />
      )
    }

    return (
      <div className={clsx(classes.img, classes.imgIcon)}>
        <InsertDriveFileIcon color="action" fontSize="large" />
      </div>
    )
  }

  return (
    <Card square elevation={0} className={classes.root}>
      <CardContent className={classes.content}>
        {renderImage()}
        <div className={classes.projectWrap}>
          <Link
            href={{
              pathname: URLS.projects.detail,
              query: {
                projectId: props.id,
                slugName: encodeURIComponent(props.slug_name || 'no-slug-name')
              }
            }}
            passHref
          >
            <Typography
              variant="body1"
              className={classes.projectName}
              component={MaterialLink}
              color="textPrimary"
              noWrap
            >
              {props.name}
            </Typography>
          </Link>
          <div className={classes.projectDescription}>
            <Typography
              variant="caption"
              className={classes.categoryName}
              noWrap
            >
              {props?.category?.name}
            </Typography>
            <Typography variant="caption" className={classes.cost}>
              {moneyFormat(props.cost ?? 0, true, locale)}
            </Typography>
            <Typography variant="caption" className={classes.publishStatus}>
              {isPublish ? (
                <>
                  {locale === 'ru' ? 'Опубликовано' : 'Published'}:{' '}
                  {props.publishedAt && (
                    <Typography
                      variant="caption"
                      color="primary"
                      component="span"
                    >
                      {dateFormatMask(props.publishedAt, format.shortFullDate)}
                    </Typography>
                  )}
                </>
              ) : (
                props.translate?.shop?.['my-models-page']['unpublish']
              )}
            </Typography>
          </div>
          <div className={classes.iconWrap}>
            <div className={classes.icon}>
              <VisibilityIcon color="primary" />
              <Typography variant="body1" color="primary">
                {props.countViews}
              </Typography>
            </div>
            <div className={classes.icon}>
              <StarIcon color="primary" />
              <Typography variant="body1" color="primary">
                {props.countAddToFavorite}
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.buttonWrap}>
          {isPublish ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleDraftProject}
            >
              {props.translate?.shop?.['my-models-page']['to-unpublish']}
            </Button>
          ) : (
            <Tooltip
              title={(tooltipBlockedProject || tooltipInvalidateProject) ?? ''}
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={!props.availableForPublication || props.isBlocked}
                  onClick={handlePublishProject}
                >
                  {props.translate?.shop?.['my-models-page']['to-publish']}
                </Button>
              </span>
            </Tooltip>
          )}
          <ProjectAuthorMenu
            className={classes.menu}
            projectId={props.id}
            slugName={props.slug_name}
            translate={props.translate}
          />
        </div>
      </CardContent>
    </Card>
  )
}
